<template>
  <v-autocomplete data-testid="select_language"
    v-model="selectedLanguage"
    :label="$t('common.language')"
    :readonly="readonly"
    :outlined="outlined"
    :dense="dense"
    :items="languageOptions"
    @change="emitLanguageChange"
  >
    <template v-slot:item="data">
      <v-list-item-content>
        <v-list-item-title>{{ data.item.text }}</v-list-item-title>
        <v-list-item-subtitle v-if="data.item.subtitle">{{ data.item.subtitle }}</v-list-item-subtitle>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
import languages from "../../../data/lang_codes.json";

export default {
  props: {
    value: String, // v-model from parent
    readonly: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    limit_languages: {
      type: Array,
      default: () => [],
    },
    require_speech_to_text: {
      type: Boolean,
      default: false,
    },
    exclude_large_character_set: {
      type: Boolean,
      default: false,
    },
    show_alphabet: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedLanguage: this.value,
    };
  },
  computed: {
    languageOptions() {
      return Object.keys(languages)
        .filter((key) => {
          if (this.limit_languages.length > 0 && !this.limit_languages.includes(key)) {
            return false;
          }
          const language = languages[key];
          if (this.require_speech_to_text && !language.speech_to_text) {
            return false;
          }
          if (this.exclude_large_character_set && language.large_character_set) {
            return false;
          }
          return true;
        })
        .map((key) => {
          let text = languages[key].nativeName;
          if (languages[key].nativeName !== languages[key].name) {
            text += ` (${languages[key].name})`;
          }
          const option = { value: key, text };
          if (this.show_alphabet) {
            option.subtitle = `${this.$t("common.alphabet")}: ${this.alphabet_name(languages[key])}`;
          }
          return option;
        }).sort((a, b) => {
            // Selected language goes first, otherwise alphabetical
            if (a.value === this.selectedLanguage) {
                return -1;
            }
            else if (b.value === this.selectedLanguage) {
                return 1;
            }
            else return a.text.localeCompare(b.text)
        });
    },
  },
  watch: {
    value(newVal) {
      this.selectedLanguage = newVal;
    },
  },
  methods: {
    /**
     * Name of the script a language is written in, such as "Latin" or "Cyrillic".
     *
     * Languages that do not name a script are written in the Latin one. Scripts with no
     * `common.alphabet_*` translation fall back to their identifier from the language table,
     * such as "devanagari".
     */
    alphabet_name(entry) {
      const alphabet = entry.alphabet || "latin";
      const key = `common.alphabet_${alphabet}`;
      // `$te` searches a single locale rather than the fallback chain, so a locale that inherits
      // its script names (such as `en-visionx`) has to be checked against the fallback as well.
      const translated = this.$te(key) || this.$te(key, this.$i18n.fallbackLocale);
      return translated ? this.$t(key) : alphabet;
    },
    emitLanguageChange() {
      this.$emit("input", this.selectedLanguage);
    },
  },
};
</script>
